import React, { useEffect, useMemo, useState } from 'react';
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { extractApiError } from '../../utils/errorMessages';
import { fetchPriceHistory } from '../../services/paperPricingService';
import {
    BATE_LABELS,
    PRICE_STATUS_LABELS,
    formatCount,
    formatDay,
    formatMonth,
    formatPrice,
    requireList,
    unitLabel,
} from './paperPricingFormat';
import { LoadProblem, StatusDot } from './PaperPricingParts';
import PaperPricingModal from './PaperPricingModal';

const LINE_COLOUR = '#4a6b25';
const INK_MUTED = '#6b7280';
const GRID = '#e5e7eb';

const axisPrice = (value) => Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 });

// Value first, day second: the reader already knows which benchmark this is.
const HistoryTooltip = ({ active, payload, unit }) => {
    if (!active || !payload || !payload.length) return null;
    const point = payload[0].payload;
    return (
        <div className="pp-chart-tip">
            <div className="pp-chart-tip-value">{formatPrice(point.raw)} {unit}</div>
            <div className="pp-chart-tip-day">
                {formatDay(point.date)}
                {point.status === 'manual' ? ' · Manual' : ''}
            </div>
        </div>
    );
};

/**
 * One benchmark's price history (ledger row 132, AC12): a line of the server's
 * prices/history answer (the last 365 days up to the latest price), with the
 * same numbers as a table.
 *
 * Only days with a price are plotted. A holiday is "no price", so the line runs
 * straight from the day before to the day after; it never dips to zero. Values
 * are turned into numbers only to draw the line -- every figure a person reads
 * is the server's own string.
 */
const PriceHistoryModal = ({ benchmark, bate, onClose }) => {
    const [history, setHistory] = useState(null);
    const [error, setError] = useState('');
    const [view, setView] = useState('chart');
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setError('');
        fetchPriceHistory({ benchmarkId: benchmark.id, bate })
            .then((data) => {
                requireList(data, 'points');
                if (!cancelled) setHistory(data);
            })
            .catch((err) => {
                if (!cancelled) setError(extractApiError(err, 'The price history could not be loaded. Try again.'));
            });
        return () => {
            cancelled = true;
        };
    }, [benchmark.id, bate, attempt]);

    const points = useMemo(() => (history ? history.points : []), [history]);
    const data = useMemo(
        () => points.map((point) => ({
            date: point.date,
            value: Number(point.value),
            raw: point.value,
            status: point.status,
        })),
        [points],
    );
    // One tick per month: the first plotted day of each month.
    const monthTicks = useMemo(() => {
        const seen = new Set();
        return data.filter((point) => {
            const month = point.date.slice(0, 7);
            if (seen.has(month)) return false;
            seen.add(month);
            return true;
        }).map((point) => point.date);
    }, [data]);

    const pointLabel = BATE_LABELS[history?.bate || bate] || bate;
    const unit = unitLabel(benchmark.currency, benchmark.unit);
    const latest = points.length ? points[points.length - 1] : null;

    return (
        <PaperPricingModal
            title={`${benchmark.code} · ${benchmark.name}`}
            eyebrow="Price history"
            onClose={onClose}
            wide
            footer={(
                <button type="button" className="pp-btn pp-btn--secondary" onClick={onClose}>Close</button>
            )}
        >
            {error ? (
                <LoadProblem message={error} onRetry={() => setAttempt((count) => count + 1)} />
            ) : !history ? (
                <p className="pp-state">Loading the price history…</p>
            ) : points.length === 0 ? (
                <p className="pp-state">
                    No {pointLabel} prices for {benchmark.code} yet. Import a price file or enter one by hand.
                </p>
            ) : (
                <>
                    <div className="pp-history-head">
                        <p className="pp-history-meta">
                            {pointLabel} prices in {unit}, {formatDay(points[0].date)} to {formatDay(latest.date)}
                            {' · '}{formatCount(points.length)} {points.length === 1 ? 'price' : 'prices'}
                            <br />
                            Latest: <strong className="pp-num-inline">{formatPrice(latest.value)}</strong> on {formatDay(latest.date)}
                        </p>
                        <div className="pp-segmented" role="tablist" aria-label="Show as">
                            {['chart', 'table'].map((option) => (
                                <button
                                    key={option}
                                    type="button"
                                    role="tab"
                                    aria-selected={view === option}
                                    className={`pp-segmented-btn ${view === option ? 'pp-segmented-btn--active' : ''}`}
                                    onClick={() => setView(option)}
                                >
                                    {option === 'chart' ? 'Chart' : 'Table'}
                                </button>
                            ))}
                        </div>
                    </div>

                    {view === 'chart' ? (
                        <div className="pp-chart" role="img" aria-label={`${benchmark.code} ${pointLabel} prices over time`}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={data} margin={{ top: 12, right: 16, bottom: 4, left: 4 }}>
                                    <CartesianGrid vertical={false} stroke={GRID} />
                                    <XAxis
                                        dataKey="date"
                                        ticks={monthTicks}
                                        tickFormatter={formatMonth}
                                        interval="preserveStartEnd"
                                        minTickGap={24}
                                        tick={{ fontSize: 11, fill: INK_MUTED }}
                                        axisLine={{ stroke: GRID }}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        domain={['auto', 'auto']}
                                        tickFormatter={axisPrice}
                                        tick={{ fontSize: 11, fill: INK_MUTED }}
                                        axisLine={false}
                                        tickLine={false}
                                        width={64}
                                    />
                                    <Tooltip
                                        cursor={{ stroke: '#94a3b8', strokeWidth: 1 }}
                                        content={<HistoryTooltip unit={unit} />}
                                        isAnimationActive={false}
                                    />
                                    <Line
                                        type="linear"
                                        dataKey="value"
                                        stroke={LINE_COLOUR}
                                        strokeWidth={2}
                                        strokeLinejoin="round"
                                        strokeLinecap="round"
                                        dot={false}
                                        activeDot={{ r: 4, fill: LINE_COLOUR, stroke: '#ffffff', strokeWidth: 2 }}
                                        isAnimationActive={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className="pp-table-scroll pp-table-scroll--tall">
                            <table className="pp-table pp-table--compact">
                                <thead>
                                    <tr>
                                        <th scope="col">Day</th>
                                        <th scope="col" className="pp-num">{pointLabel} ({unit})</th>
                                        <th scope="col">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...points].reverse().map((point) => (
                                        <tr key={point.date}>
                                            <td className="pp-nowrap">{formatDay(point.date)}</td>
                                            <td className="pp-num">{formatPrice(point.value)}</td>
                                            <td>
                                                <StatusDot tone={{ confirmed: 'green', manual: 'amber' }[point.status] || 'grey'}>
                                                    {PRICE_STATUS_LABELS[point.status] || point.status}
                                                </StatusDot>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </PaperPricingModal>
    );
};

export default PriceHistoryModal;
